import { Picker } from "@design-system/Picker/Picker.lib";
import { OptionToggle } from "./playgroundControls";
import type {
    PickerChevron,
    PickerEmptyBehavior,
    PickerFlyoutAutoOpen,
    PickerFlyoutQueryScope,
    PickerNested,
    PickerParentSelection,
    PickerSelectionSummary,
    PickerStatus,
} from "@design-system/Picker/Picker.types";
import {
    pickerPlaygroundChevrons,
    pickerPlaygroundEmptyBehaviors,
    pickerPlaygroundFlyoutScopes,
    pickerPlaygroundFlyoutQueryScopes,
    pickerPlaygroundFlyoutAutoOpens,
    pickerPlaygroundNested,
    pickerPlaygroundParentSelections,
    pickerPlaygroundStatuses,
    pickerPlaygroundSummaryModes,
    pickerPlaygroundTriggers,
    pickerPlaygroundButtonVariants,
    type PickerPlaygroundButtonVariant,
    type PickerPlaygroundTrigger,
    type PickerPlaygroundFeatures,
    type PickerPlaygroundFlyoutScope,
} from "./PickerPlayground.lib";
import styles from "./DatePickerPlayground.module.scss";

const buttonVariantItems = pickerPlaygroundButtonVariants.map((variant) => ({
    value: variant,
    label: variant,
}));

/** Triggers the stage can render: a Button element and the three presets. */
export const triggerOptionsFor = (): readonly PickerPlaygroundTrigger[] =>
    pickerPlaygroundTriggers;

/** The trigger preset the root can actually render for the current toggles. */
export const resolveTrigger = (features: PickerPlaygroundFeatures) => {
    const options = triggerOptionsFor();
    return options.includes(features.trigger) ? features.trigger : options[0];
};

const OptionGroup = <Value extends string>({
    title,
    value,
    options,
    disabled = false,
    onChange,
}: {
    title: string;
    value: Value;
    options: readonly Value[];
    disabled?: boolean;
    onChange: (value: Value) => void;
}) => (
    <>
        <h2 className={styles.subheading}>{title}</h2>
        <OptionToggle
            options={options}
            value={value}
            disabled={disabled}
            onChange={onChange}
        />
    </>
);

/**
 * The option half of the Picker sandbox aside: trigger preset,
 * async status, summary mode, nesting, parent selection, flyout scopes,
 * chevron and empty behaviour.
 */
export const PickerPlaygroundOptionControls = ({
    features,
    onFeaturesChange,
}: {
    features: PickerPlaygroundFeatures;
    onFeaturesChange: (features: PickerPlaygroundFeatures) => void;
}) => {
    const triggerOptions = triggerOptionsFor();
    const hasSummary = features.isMultiple;

    return (
        <section>
            {triggerOptions.length > 0 && (
                <OptionGroup<PickerPlaygroundTrigger>
                    title="Trigger"
                    value={resolveTrigger(features)}
                    options={triggerOptions}
                    onChange={(trigger) =>
                        onFeaturesChange({ ...features, trigger })
                    }
                />
            )}
            {features.trigger === "button" && (
                <>
                    <h2 className={styles.subheading}>Button variant</h2>
                    <Picker.Select<PickerPlaygroundButtonVariant>
                        items={buttonVariantItems}
                        value={features.buttonVariant}
                        onValueChange={(buttonVariant) => {
                            if (buttonVariant) {
                                onFeaturesChange({
                                    ...features,
                                    buttonVariant,
                                });
                            }
                        }}
                        trigger={<Picker.Button variant="primary" size="sm" />}
                    />
                </>
            )}
            <OptionGroup<PickerStatus>
                title="Status"
                value={features.status}
                options={pickerPlaygroundStatuses}
                disabled={features.isPaged}
                onChange={(status) => onFeaturesChange({ ...features, status })}
            />
            {hasSummary && (
                <OptionGroup<PickerSelectionSummary["mode"]>
                    title="Summary"
                    value={features.summaryMode}
                    options={pickerPlaygroundSummaryModes}
                    onChange={(summaryMode) =>
                        onFeaturesChange({ ...features, summaryMode })
                    }
                />
            )}
            <OptionGroup<PickerNested>
                title="Nested (with tree items)"
                value={features.nested}
                options={pickerPlaygroundNested}
                disabled={!features.isTree}
                onChange={(nested) => onFeaturesChange({ ...features, nested })}
            />
            {features.isMultiple && features.isTree && (
                <OptionGroup<PickerParentSelection>
                    title="Parent selection"
                    value={features.parentSelection}
                    options={pickerPlaygroundParentSelections}
                    onChange={(parentSelection) =>
                        onFeaturesChange({ ...features, parentSelection })
                    }
                />
            )}
            {features.nested === "flyout" && (
                <>
                    <OptionGroup<PickerPlaygroundFlyoutScope>
                        title="Flyout search"
                        value={features.flyoutSearch}
                        options={pickerPlaygroundFlyoutScopes}
                        disabled={!features.isTree}
                        onChange={(flyoutSearch) =>
                            onFeaturesChange({ ...features, flyoutSearch })
                        }
                    />
                    <OptionGroup<PickerPlaygroundFlyoutScope>
                        title="Flyout footer"
                        value={features.flyoutFooter}
                        options={pickerPlaygroundFlyoutScopes}
                        disabled={!features.isTree}
                        onChange={(flyoutFooter) =>
                            onFeaturesChange({ ...features, flyoutFooter })
                        }
                    />
                    <OptionGroup<PickerFlyoutQueryScope>
                        title="Flyout query scope"
                        value={features.flyoutQueryScope}
                        options={pickerPlaygroundFlyoutQueryScopes}
                        disabled={!features.isTree}
                        onChange={(flyoutQueryScope) =>
                            onFeaturesChange({ ...features, flyoutQueryScope })
                        }
                    />
                    <OptionGroup<PickerFlyoutAutoOpen>
                        title="Flyout auto-open"
                        value={features.flyoutAutoOpen}
                        options={pickerPlaygroundFlyoutAutoOpens}
                        disabled={!features.isTree}
                        onChange={(flyoutAutoOpen) =>
                            onFeaturesChange({ ...features, flyoutAutoOpen })
                        }
                    />
                </>
            )}
            <OptionGroup<PickerChevron | "default">
                title="Chevron"
                value={features.chevron ?? "default"}
                options={["default", ...pickerPlaygroundChevrons]}
                onChange={(chevron) =>
                    onFeaturesChange({
                        ...features,
                        chevron: chevron === "default" ? undefined : chevron,
                    })
                }
            />
            <OptionGroup<PickerEmptyBehavior>
                title="Empty behavior"
                value={features.emptyBehavior}
                options={pickerPlaygroundEmptyBehaviors}
                onChange={(emptyBehavior) =>
                    onFeaturesChange({ ...features, emptyBehavior })
                }
            />
        </section>
    );
};
